// Spec 43 design-system — density modes.
//
// Two modes per feat_ds_density_modes:
//   * comfortable (default) — generous padding + 44dp+ tap targets, the
//     baseline for general HR surfaces (hire, manage, learn).
//   * compact — one spacing step tighter, used on info-dense surfaces
//     (people directory tables, payroll registers, audit trails).
//
// The mode adjusts component padding + row heights but NEVER shrinks
// type below body-md (15px) — legibility is non-negotiable. Touch
// surfaces also preserve the 44×44 minimum tap target on mobile per
// feat_ds_accessibility_baseline.
//
// Components consume the mode via the DensityScale helper: pass the
// canonical "comfortable" spacing step + the active mode, get back the
// resolved step. Implementing this here (rather than per-component)
// keeps the down-step rule in ONE place.

import { SPACING, type SpacingName } from "@novemai/design-tokens";

export type DensityMode = "comfortable" | "compact";

export const DENSITY_MODES = ["comfortable", "compact"] as const;

/**
 * Spacing-step ordering — the SPACING registry keys in ascending order.
 * Cached at module load so resolveDensitySpacing() doesn't re-sort
 * per call (hot path during render).
 */
const SPACING_KEYS_ASC: readonly SpacingName[] = (
  Object.keys(SPACING) as SpacingName[]
).sort((a, b) => SPACING[a] - SPACING[b]);

/**
 * Resolve a spacing step under the active density mode. Compact mode
 * shifts one step down (16→12, 12→8, 8→4, ...) UNTIL we hit the floor.
 * Floor is space_2 (4px) — anything tighter is unreadable padding.
 *
 * Comfortable mode is identity — passes the input through unchanged.
 */
export function resolveDensitySpacing(
  step: SpacingName,
  mode: DensityMode,
): SpacingName {
  if (mode === "comfortable") return step;
  const idx = SPACING_KEYS_ASC.indexOf(step);
  if (idx < 0) return step; // unknown — return as-is
  // Compact = one step down, but stop at space_2 (the readability floor).
  // Steps already at or below the floor (space_0/1/2) pass through
  // unchanged — we don't snap them UP toward the floor (that would
  // expand padding when the caller asked for tighter).
  const FLOOR_INDEX = SPACING_KEYS_ASC.indexOf("space_2");
  if (idx <= FLOOR_INDEX) return step;
  return SPACING_KEYS_ASC[idx - 1] ?? step;
}

/**
 * Resolve a row-height target under the active mode. Comfortable rows
 * are 48dp (table cells), compact rows are 36dp. Both clear the
 * 44×44 mobile tap target via the column stretching to full width
 * (touch happens at full row, not just the 36dp height).
 *
 * Returns px (integer) — no token form because row heights are a
 * compound of spacing + line-height + padding, computed per-component.
 */
export function resolveRowHeightDp(mode: DensityMode): number {
  return mode === "comfortable" ? 48 : 36;
}

/**
 * Document the rule for "should this surface USE compact?" so module
 * authors picking a density don't relitigate. Returns `true` when the
 * surface is information-dense (per spec 43 feat_ds_density_modes
 * AC: tables, registers, audit trails, payroll runs).
 */
export const COMPACT_SURFACES = new Set([
  "table",
  "list_dense",
  "audit_trail",
  "payroll_register",
  "directory",
  "report_grid",
]);

export function shouldDefaultToCompact(surfaceKind: string): boolean {
  return COMPACT_SURFACES.has(surfaceKind);
}
